import React, { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { getOrderDeatilsDB } from 'slice/orderSliceDB';
import { useParams } from 'react-router-dom';
import storeLogo from '../../asserts/img/logodry.png';

const STORE_INFO = {
  name: 'TN Dry Fruits',
  tagline: 'Pure. Netural. Premium Dry Fruits & Gift Boxes',
  address: 'Shop No. 12, Akbar Ward, Dalda Factory Road, Barapathar, Seoni, Madhya Pradesh - 480661',
  phone: '+91 8989561080, +91 8770280435',
  email: 'tnorganicdryfruits@gmail.com',
  website: 'tnorganicdryfruits.com',
  gstin: 'GSTIN: N/A',
  logo: storeLogo,
  logoFallback: 'https://tnorganicdryfruits.com/assets/logodry.png',
};

const logoToDataUrl = async () => {
  try {
    const res = await fetch(STORE_INFO.logo);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return STORE_INFO.logoFallback;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return dateStr; }
};

const statusBadge = (status) => {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    processing: 'bg-blue-100 text-blue-800',
    dispatched: 'bg-purple-100 text-purple-800',
    delivered: 'bg-green-100 text-green-800',
    complete: 'bg-green-100 text-green-800',
    reject: 'bg-red-100 text-red-800',
    cancelled: 'bg-red-100 text-red-800',
    cancelByUser: 'bg-red-100 text-red-800',
  };
  const color = colors[(status || '').toLowerCase()] || 'bg-gray-100 text-gray-800';
  return <span className={`px-2 py-1 rounded-full text-xs font-bold ${color}`}>{status || 'N/A'}</span>;
};

const BillDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch()
  const printRef = useRef();
  const cartData = useSelector((state) => state.orderDB);
  useEffect(() => {
    dispatch(getOrderDeatilsDB(id))
  }, [dispatch, id])

  const ITEM_KEYS = ['items', 'order_items', 'orderItems', 'cart_items', 'products', 'itemData1', 'product_items', 'productItems'];

  const extractItems = (orderPayload, dataPayload) => {
    const candidates = [orderPayload, dataPayload, dataPayload?.data, dataPayload?.order, dataPayload?.orderData];
    for (const obj of candidates) {
      if (!obj) continue;
      for (const key of ITEM_KEYS) {
        if (Array.isArray(obj?.[key])) return obj[key];
      }
    }
    return [];
  };

  const normalized = () => {
    const data = cartData?.data || {};
    const listFallback = Array.isArray(data)
      ? data.find((o) => String(o.id || o.order_id || o._id) === String(id))
      : undefined;
    const order =
      data.order || data.data?.order || data.orderData ||
      data.orders?.find?.((o) => String(o.id || o.order_id || o._id) === String(id)) ||
      listFallback || data;
    const customer = data.customerData || data.customer || order?.customer || order?.user || {};
    const items = extractItems(order, data);
    return { order, customer, items };
  };

  const { order, customer, items } = normalized();

  const getItemName = (item) =>
    item?.item_name || item?.product?.name || item?.product?.product_name ||
    item?.product?.title || item?.product_id?.name || item?.product_id?.title ||
    item?.productId?.name || item?.productId?.title || item?.product_name ||
    item?.name || item?.title || 'Item';

  const toNumber = (value) => { const n = Number(value); return Number.isFinite(n) ? n : 0; };
  const getItemQty = (item) => toNumber(item?.quantity ?? item?.qty ?? item?.count ?? 1) || 1;

  const getItemUnitPrice = (item) => {
    if (item?.price !== undefined && item?.price !== null && item?.price !== '') return toNumber(item.price);
    if (item?.unit_price !== undefined && item?.unit_price !== null) return toNumber(item.unit_price);
    if (item?.rate !== undefined && item?.rate !== null) return toNumber(item.rate);
    if (item?.sprice !== undefined && item?.sprice !== null) return toNumber(item.sprice);
    if (item?.product?.selling_price !== undefined && item?.product?.selling_price !== null) return toNumber(item.product.selling_price);
    return 0;
  };

  const getItemMRP = (item) => {
    const candidates = [
      item?.original_price, item?.mrp, item?.list_price,
      item?.product?.original_price, item?.product?.mrp, item?.product?.list_price,
    ];
    const mrp = candidates.find((v) => v !== undefined && v !== null && v !== '');
    const mrpNum = toNumber(mrp);
    if (mrpNum > 0 && mrpNum >= getItemUnitPrice(item)) return mrpNum;
    return getItemUnitPrice(item);
  };

  const getItemLineTotal = (item) => {
    if (item?.subtotal !== undefined && item?.subtotal !== null && item?.subtotal !== '') {
      return toNumber(item.subtotal);
    }
    return getItemQty(item) * getItemUnitPrice(item);
  };

  const getItemSavings = (item) =>
    Math.max(0, (getItemMRP(item) - getItemUnitPrice(item)) * getItemQty(item));

  const fmt = (n) => '₹' + toNumber(n).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const itemSubtotal = items.reduce((acc, item) => acc + getItemLineTotal(item), 0);
  const mrpTotal = items.reduce((acc, item) => acc + getItemMRP(item) * getItemQty(item), 0);
  const totalDiscount = Math.max(0, mrpTotal - itemSubtotal);

  const amountPaid = toNumber(order?.total_amount ?? order?.total ?? order?.amount ?? order?.totalAmount ?? itemSubtotal);

  let shippingCharge = toNumber(order?.shipping_charge ?? order?.shippingCharge ?? 0);
  if (shippingCharge <= 0) {
    const derived = amountPaid - itemSubtotal;
    shippingCharge = derived > 0 ? derived : 0;
  }

  const grandTotal = amountPaid || (itemSubtotal + shippingCharge);
  const itemCount = items.reduce((acc, item) => acc + getItemQty(item), 0);

  const customerName = customer?.name || order?.guest_name || 'Customer';
  const customerPhone = customer?.mobile || customer?.phone || order?.phone || 'N/A';
  const customerEmail = customer?.email || '';

  const buildAddress = (...parts) => parts.filter(Boolean).join(', ') || 'N/A';

  const billingAddress = buildAddress(
    order?.shipping_address || customer?.address,
    order?.flatno,
    order?.landmark,
    order?.city || customer?.city,
    order?.state || customer?.state,
    order?.pincode || customer?.pincode,
  );

  const orderId = order?.order_id || order?.id || 'N/A';
  const invoiceNo = order?.invoice_no || order?.invoiceNumber || orderId;

  const handlePrint = async () => {
    let printContents = printRef.current.innerHTML;
    const printWindow = window.open("", "", "width=1000,height=700");
    const logoDataUrl = await logoToDataUrl();
    printContents = printContents.replace(/<img[^>]*src="[^"]*"[^>]*>/, (match) =>
      match.replace(/src="[^"]*"/, `src="${logoDataUrl}"`)
    );
    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice - ${STORE_INFO.name}</title>
          <style>
            @page { margin: 10mm; size: A4; }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Segoe UI', Arial, sans-serif; color: #222; line-height: 1.5; font-size: 12px; background: #fff; }
            .inv-wrap { max-width: 820px; margin: 0 auto; background: #fff; }
            .inv-topbar { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; padding: 18px 24px; background: #143a24; color: #fff; border-radius: 6px 6px 0 0; }
            .inv-brand .logo-row { display: flex; align-items: center; gap: 12px; }
            .inv-brand img { height: 52px; width: 52px; object-fit: contain; background: #fff; border-radius: 50%; padding: 5px; border: 2px solid #ffd54f; }
            .inv-brand h1 { font-size: 22px; letter-spacing: 1px; margin: 0; color: #fff; }
            .inv-brand p { font-size: 11px; color: #cfe8d8; margin: 2px 0 0; }
            .inv-meta { text-align: right; }
            .inv-meta h2 { font-size: 18px; margin: 0 0 4px; color: #ffd54f; letter-spacing: 2px; }
            .inv-meta p { font-size: 11px; margin: 1px 0; color: #cfe8d8; }
            .inv-body { padding: 20px 24px; }
            .info-grid { display: flex; gap: 24px; margin-bottom: 18px; flex-wrap: wrap; }
            .seller-box { padding: 12px 14px; min-width: 300px; flex: 1; }
            .info-col {  padding: 12px 14px; flex: 1; min-width: 280px; }
            .info-label { font-size: 10px; font-weight: 800; color: #143a24; text-transform: uppercase; letter-spacing: 1px; border-bottom: 2px solid #143a24; padding-bottom: 5px; margin-bottom: 8px; }
            .info-label.gray { color: #666; border-color: #ccc; }
            .info-row-line { display: flex; justify-content: space-between; font-size: 12px; margin: 3px 0; }
            .info-row-line .k { color: #777; }
            .info-row-line .v { font-weight: 600; color: #222; }
            .mt-2 { margin-top: 6px; }
            table { width: 100%; border-collapse: collapse; margin: 10px 0 16px; }
            thead th { background: #f0f4f1; color: #143a24; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; padding: 8px 6px; border: 1px solid #e0e0e0; text-align: left; }
            thead th.right { text-align: right; }
            thead th.center { text-align: center; }
            td { padding: 8px 6px; border: 1px solid #e0e0e0; font-size: 11.5px; vertical-align: top; }
            td.right { text-align: right; }
            td.center { text-align: center; }
            .p-name { font-weight: 600; color: #222; }
            .p-mrp { color: #999; text-decoration: line-through; font-size: 10.5px; }
            .p-price { font-weight: 700; color: #143a24; }
            .p-save { color: #15803d; font-weight: 700; font-size: 11px; }
            .bottom-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; flex-wrap: wrap; }
            .note-box { flex: 1; min-width: 240px; }
            .note-box p { font-size: 11px; color: #555; margin: 2px 0; }
            .price-panel { width: 320px; border: 1px solid #e0e0e0; border-radius: 6px; overflow: hidden; }
            .price-panel h4 { background: #f0f4f1; margin: 0; padding: 8px 12px; font-size: 12px; color: #143a24; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #e0e0e0; }
            .price-panel .row { display: flex; justify-content: space-between; padding: 6px 12px; font-size: 12px; border-bottom: 1px dashed #e0e0e0; }
            .price-panel .row .l { color: #555; }
            .price-panel .row .v { font-weight: 600; }
            .price-panel .row.discount .v { color: #15803d; font-weight: 700; }
            .price-panel .row.delivery .v.free { color: #15803d; font-weight: 700; }
            .price-panel .grand { display: flex; justify-content: space-between; padding: 10px 12px; background: #f0f4f1; font-weight: 800; font-size: 14px; color: #143a24; }
            .savings-line { text-align: right; padding: 8px 12px; background: #e8f5e9; color: #15803d; font-weight: 700; font-size: 12px; border-top: 1px solid #e0e0e0; }
            .inv-footer { padding: 14px 24px; border-top: 2px solid #143a24; text-align: center; font-size: 11px; color: #888; background: #fafafa; border-radius: 0 0 6px 6px; }
            .inv-footer p { margin: 2px 0; }
            .inv-footer .contact { color: #555; }
          </style>
        </head>
        <body>
          <div class="inv-wrap">
            ${printContents}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 300);
  };

  return (
    <>
    <style>{`
      @media print {
        .no-print { display: none !important; }
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
      .invoice-container { max-width: 900px; margin: 0 auto; background: #fff; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,0.08); }
      .inv-topbar { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; padding: 18px 24px; background: #143a24; color: #fff; border-radius: 8px 8px 0 0; }
      .inv-brand h1 { font-size: 22px; letter-spacing: 1px; margin: 0; color: #fff; }
      .inv-brand p { font-size: 11px; color: #cfe8d8; margin: 2px 0 0; }
      .inv-brand .logo-row { display: flex; align-items: center; gap: 12px; }
      .inv-brand img { height: 102px; width: 102px; object-fit: contain; border-radius: 50%; background: #fff; }
      .inv-meta { text-align: right; }
      .inv-meta h2 { font-size: 18px; margin: 0 0 4px; color: #ffd54f; letter-spacing: 2px; }
      .inv-meta p { font-size: 11px; margin: 1px 0; color: #cfe8d8; }
      .inv-body { padding: 20px 24px; }
      .info-grid { display: flex; gap: 20px; margin-bottom: 18px; flex-wrap: wrap; }
      .seller-box { padding: 12px 14px; flex: 1; min-width: 280px; }
      .info-col {  padding: 12px 14px; flex: 1; min-width: 280px; }
      .info-label { font-size: 10px; font-weight: 800; color: #143a24; text-transform: uppercase; letter-spacing: 1px; border-bottom: 2px solid #143a24; padding-bottom: 5px; margin-bottom: 8px; }
      .info-label.gray { color: #666; border-color: #ccc; }
      .info-row-line { display: flex; justify-content: space-between; font-size: 13px; margin: 4px 0; }
      .info-row-line .k { color: #777; }
      .info-row-line .v { font-weight: 600; color: #222; }
      .mt-2 { margin-top: 6px; }
      .items-table { width: 100%; border-collapse: collapse; margin: 10px 0 16px; }
      .items-table thead th { background: #f0f4f1; color: #143a24; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; padding: 9px 8px; border: 1px solid #e0e0e0; text-align: left; }
      .items-table thead th.right { text-align: right; }
      .items-table thead th.center { text-align: center; }
      .items-table td { padding: 9px 8px; border: 1px solid #e0e0e0; font-size: 13px; vertical-align: top; }
      .items-table td.right { text-align: right; }
      .items-table td.center { text-align: center; }
      .p-name { font-weight: 600; color: #222; }
      .p-mrp { color: #999; text-decoration: line-through; font-size: 11.5px; }
      .p-price { font-weight: 700; color: #143a24; }
      .p-save { color: #15803d; font-weight: 700; font-size: 12px; }
      .bottom-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; flex-wrap: wrap; }
      .note-box { flex: 1; min-width: 240px; }
      .note-box p { font-size: 12px; color: #555; margin: 3px 0; }
      .note-box p strong { color: #222; }
      .price-panel { width: 340px; border: 1px solid #e0e0e0; border-radius: 6px; overflow: hidden; }
      .price-panel h4 { background: #f0f4f1; margin: 0; padding: 9px 12px; font-size: 12.5px; color: #143a24; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #e0e0e0; }
      .price-panel .row { display: flex; justify-content: space-between; padding: 7px 12px; font-size: 13px; border-bottom: 1px dashed #e0e0e0; }
      .price-panel .row .l { color: #555; }
      .price-panel .row .v { font-weight: 600; }
      .price-panel .row.discount .v { color: #15803d; font-weight: 700; }
      .price-panel .row.delivery .v.free { color: #15803d; font-weight: 700; }
      .price-panel .grand { display: flex; justify-content: space-between; padding: 11px 12px; background: #f0f4f1; font-weight: 800; font-size: 15px; color: #143a24; }
      .savings-line { text-align: right; padding: 9px 12px; background: #e8f5e9; color: #15803d; font-weight: 700; font-size: 12.5px; border-top: 1px solid #e0e0e0; }
      .inv-footer { padding: 14px 24px; border-top: 2px solid #143a24; text-align: center; font-size: 12px; color: #888; background: #fafafa; border-radius: 0 0 8px 8px; }
      .inv-footer p { margin: 2px 0; }
      .inv-footer .contact { color: #555; }
      .btn-print { background: #143a24; color: #fff; border: none; padding: 8px 20px; border-radius: 6px; font-size: 13px; cursor: pointer; }
      .btn-print:hover { background: #2d6a3f; }
    `}</style>

    <div className="invoice-container my-5">
      <div className="no-print d-flex justify-end mb-3" style={{ padding: '0 24px', paddingTop: 20 }}>
        <button onClick={handlePrint} className="btn-print">🖨️ Print / Download PDF</button>
      </div>

      <div ref={printRef}>
        {/* ── TOP BAR ── */}
        <div className="inv-topbar">
          <div className="inv-brand">
            <div className="logo-row">
              <img src={STORE_INFO.logo} alt="logo" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              <div>
                <h1>{STORE_INFO.name.toUpperCase()}</h1>
                <p>{STORE_INFO.tagline}</p>
              </div>
            </div>
          </div>
          <div className="inv-meta">
            <h2>TAX INVOICE</h2>
            <p><strong>Invoice No:</strong> #{invoiceNo}</p>
            <p><strong>Date:</strong> {formatDate(order?.createdAt)}</p>
            <p><strong>Order No:</strong> #{orderId}</p>
          </div>
        </div>

        {/* ── BODY ── */}
        <div className="inv-body">
          {/* Info */}
          <div className="info-grid">
            {/* Sold By */}
            <div className="seller-box">
              <div className="info-label">Sold By</div>
              <div className="info-row-line"><span className="k">Seller:</span><span className="v">{STORE_INFO.name}</span></div>
              <div className="info-row-line"><span className="k">Address:</span><span className="v">{STORE_INFO.address}</span></div>
              <div className="info-row-line"><span className="k">Phone:</span><span className="v">{STORE_INFO.phone}</span></div>
              <div className="info-row-line"><span className="k">Email:</span><span className="v">{STORE_INFO.email}</span></div>
              <div className="info-row-line"><span className="k">Website:</span><span className="v">{STORE_INFO.website}</span></div>
            </div>

            {/* Bill To */}
            <div className="info-col">
              <div className="info-label">Bill To / Ship To</div>
              <div className="info-row-line"><span className="k">Name:</span><span className="v">{customerName}</span></div>
              <div className="info-row-line"><span className="k">Phone:</span><span className="v">{customerPhone}</span></div>
              {customerEmail ? <div className="info-row-line"><span className="k">Email:</span><span className="v">{customerEmail}</span></div> : null}
              <div className="info-row-line"><span className="k">Address:</span><span className="v">{billingAddress}</span></div>
            </div>

            {/* Order Details */}
            <div className="info-col">
              <div className="info-label gray">Order Details</div>
              <div className="info-row-line"><span className="k">Order ID:</span><span className="v">#{orderId}</span></div>
              <div className="info-row-line"><span className="k">Date:</span><span className="v">{formatDate(order?.createdAt)}</span></div>
              <div className="info-row-line"><span className="k">Payment:</span><span className="v">{(order?.payment_method || 'N/A').toUpperCase()}</span></div>
              <div className="info-row-line"><span className="k">Transaction ID:</span><span className="v">{order?.transaction_id === 'COD' ? 'COD' : (order?.transaction_id || 'N/A')}</span></div>
              <div className="info-row-line"><span className="k">Type:</span><span className="v">{(order?.order_type || 'N/A').toUpperCase()}</span></div>
              <div className="info-row-line mt-2"><span className="k">Status:</span><span className="v">{statusBadge(order?.status)}</span></div>
            </div>
          </div>

          {/* Items Table */}
          <table className="items-table">
            <thead>
              <tr>
                <th style={{ width: '5%' }} className="center">#</th>
                <th style={{ width: '32%' }}>Product</th>
                <th style={{ width: '7%' }} className="center">Qty</th>
                <th style={{ width: '14%' }} className="right">MRP</th>
                <th style={{ width: '14%' }} className="right">Price</th>
                <th style={{ width: '15%' }} className="right">Amount</th>
                <th style={{ width: '13%' }} className="right">Savings</th>
              </tr>
            </thead>
            <tbody>
              {items?.length ? items.map((item, key) => {
                const mrp = getItemMRP(item);
                const price = getItemUnitPrice(item);
                return (
                  <tr key={key}>
                    <td className="center" style={{ color: '#999' }}>{key + 1}</td>
                    <td className="p-name">{getItemName(item)}</td>
                    <td className="center">{getItemQty(item)}</td>
                    <td className="right"><span className="p-mrp">{fmt(mrp)}</span></td>
                    <td className="right"><span className="p-price">{fmt(price)}</span></td>
                    <td className="right">{fmt(getItemLineTotal(item))}</td>
                    <td className="right">
                      {getItemSavings(item) > 0 ? <span className="p-save">− {fmt(getItemSavings(item))}</span> : '—'}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 20, color: '#999' }}>No items found</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Bottom: Note + Price Details */}
          <div className="bottom-row">
            <div className="note-box">
              <p><strong>Payment Status:</strong> {order?.payment_method === 'cod' ? 'Cash on Delivery' : 'Prepaid / Online'}</p>
              <p><strong>Total Items:</strong> {itemCount} item{itemCount !== 1 ? 's' : ''}</p>
              <p style={{ marginTop: 10, color: '#999' }}>Thank you for shopping with {STORE_INFO.name}. For any query regarding your order, contact us at {STORE_INFO.phone} or {STORE_INFO.email}.</p>
            </div>

            <div className="price-panel">
              <h4>Price Details ({itemCount} item{itemCount !== 1 ? 's' : ''})</h4>
              <div className="row"><span className="l">Price ({itemCount} item{itemCount !== 1 ? 's' : ''})</span><span className="v">{fmt(mrpTotal)}</span></div>
              {totalDiscount > 0 ? (
                <div className="row discount"><span className="l">Discount</span><span className="v">− {fmt(totalDiscount)}</span></div>
              ) : null}
              <div className="row delivery">
                <span className="l">Delivery / Shipping Charges</span>
                {shippingCharge > 0 ? <span className="v">{fmt(shippingCharge)}</span> : <span className="v free">FREE</span>}
              </div>
              <div className="grand">
                <span>Amount Paid</span>
                <span>{fmt(grandTotal)}</span>
              </div>
              {/* {totalDiscount > 0 ? (
                <div className="savings-line">🎉 You saved {fmt(totalDiscount)} on this order</div>
              ) : null} */}
            </div>
          </div>
        </div>

        {/* ── FOOTER ── */}
        <div className="inv-footer">
          <p><strong>{STORE_INFO.name}</strong> — {STORE_INFO.tagline}</p>
          <p>{STORE_INFO.address}</p>
          <p className="contact">Call: {STORE_INFO.phone} &nbsp;|&nbsp; Email: {STORE_INFO.email} &nbsp;|&nbsp; {STORE_INFO.website}</p>
          <p style={{ fontSize: 11, marginTop: 4, color: '#aaa' }}>Thank you for your order!</p>
        </div>
      </div>
    </div>
    </>
  )
}

export default BillDetails